import { Box, Button, Card, CardContent, Chip, TextField } from "@mui/material";
import { Add, ArrowBack, Delete, Edit } from "@mui/icons-material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import localize from "../../../common/localize";
import { generateNoDataValues, getFieldTypeName } from "../../../common/utils";
import DeleteConfirmationMessage from "../../../components/common/DeleteConfirmationMessage";
import ErrorMessage from "../../../components/common/ErrorMessage";
import ModalBackground from "../../../components/common/ModalBackground";
import TableComponent from "../../../components/common/TableComponent";
import PageTitleSubtitle from "../../../components/PageTitleSubTitle";
import useGlobalContext from "../../../hooks/useGlobalContext";
import AddOrEditPage from "./AddOrEditPage";
import AddOrEditFormField from "./AddOrEditFormField";
import {
  getClearBasicModel,
  getclearFormFieldModel,
} from "../../../common/EntitiesManagement";
import TrueFalseImage from "../../../components/TrueFalseImage";
import ValueOrNoDataLabel from "../../../components/common/ValueOrNoDataLabel";
import ActionButtons from "../../../components/common/ActionButtons";
import LoadingPanel from "../../../components/common/LoadingPanel";
import NoDataPage from "../../../components/noDataPage/NoDataPage";
import ReportSectionTitle from "../../../components/common/ReportSectionTitle";
import ScrollablePageTab from "./ScrollablePageTab";

const FormBuilder = () => {
  const {
    dynamicFormModel,
    setDynamicFormModel,
    setFormFieldModel,
    formFieldModel,
    dynamicFormPages,
    setDynamicFormPages,
    setSelectedPage,
    selectedPage,
    dynamicFormData,
    DYNAMIC_FORM_ENDPOINT,
    config,
    pageModel,
    setPageModel,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
  } = useGlobalContext();
  const navigate = useNavigate();
  const [showAddPageModal, setShowAddPageModal] = useState(false);
  const [showEditPageModal, setShowEditPageModal] = useState(false);
  const [showAddFormFieldModal, setShowAddFormFieldModal] = useState(false);
  const [showDeleteFieldModal, setShowDeleteFieldModal] = useState(false);
  const [showDeletePageModal, setShowDeletePageModal] = useState(false);
  const [showEditFormFieldModal, setShowEditFormFieldModal] = useState(false);
  const [formFieldDataFiltered, setFormFieldDataFiltered] = useState([]);
  const [formFieldDataRaw, setFormFieldDataRaw] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { id } = useParams();

  const searcher = (e) => {
    setSearch(e.target.value);
  };

  const validateOperationResult = (result, isDelete) => {
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result?.data || result?.Data));
      setShowErrorModal(true);
    }
  };

  const validateOperationResultRaw = (result, isDelete) => {
    if (!result.Ok) {
      setErrorMessage(JSON.stringify(result?.data || result?.Data));
      setShowErrorModal(true);
    }
  };

  const getDynamicFormPages = async () => {
    setLoading(true);

    try {
      const data = await axios.get(
        `${DYNAMIC_FORM_ENDPOINT}/${id}/page`,
        config
      );
      setLoading(false);
      validateOperationResultRaw(data.data);
      setDynamicFormPages(data.data.Data);
    } catch (error) {
      setLoading(false);
      validateOperationResult(error.response.data);
    }
  };

  const getDynamicFormPageFields = async () => {
    try {
      setLoading(true);
      const data = await axios.get(
        `${DYNAMIC_FORM_ENDPOINT}/${id}/page/${dynamicFormPages[selectedPage].Id}/field`,
        config
      );
      setLoading(false);

      setFormFieldDataFiltered(data?.data?.Data);
      setFormFieldDataRaw(data?.data?.Data);
      validateOperationResultRaw(data?.data);
    } catch (error) {
      setLoading(false);
      validateOperationResult({ data: error.message });
    }
  };

  const handleCreateDynamicFormPageField = async () => {
    const updateResult = await createEntity(
      `${DYNAMIC_FORM_ENDPOINT}/${id}/page/${dynamicFormPages[selectedPage].Id}/field`,
      { ...formFieldModel, PageId: dynamicFormPages[selectedPage].Id },
      config
    );
    getDynamicFormPageFields();
    validateOperationResult(updateResult, false);
    setFormFieldModel(getclearFormFieldModel());
  };

  const handleUpdateDynamicFormPageField = async () => {
    const updateResult = await updateEntity(
      `${DYNAMIC_FORM_ENDPOINT}/${id}/page/${dynamicFormPages[selectedPage].Id}/field/${formFieldModel.FormField.Id}`,
      { ...formFieldModel, PageId: dynamicFormPages[selectedPage].Id },
      config
    );
    getDynamicFormPageFields();
    validateOperationResult(updateResult, false);
    setFormFieldModel(getclearFormFieldModel());
  };

  const handleDeleteDynamicFormPageField = async () => {
    const deleteResult = await deleteEntity(
      `${`${DYNAMIC_FORM_ENDPOINT}/${id}/page/${dynamicFormPages[selectedPage].Id}/field/${formFieldModel.FormField.Id}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
    setShowDeleteFieldModal(false);
    setFormFieldModel(getclearFormFieldModel());
    getDynamicFormPages();
  };

  const handleCreatePage = async (data) => {
    const updateResult = await createEntity(
      `${DYNAMIC_FORM_ENDPOINT}/${id}/page`,
      data,
      config
    );
    getDynamicFormPages();
    validateOperationResult(updateResult, false);
    setPageModel({});
  };

  const handleEditPage = async (data) => {
    const updateResult = await updateEntity(
      `${DYNAMIC_FORM_ENDPOINT}/${id}/page/${data.Id}`,
      data,
      config
    );
    getDynamicFormPages();
    validateOperationResult(updateResult, false);
    setPageModel({});
  };

  const handleDeletePage = async () => {
    const deleteResult = await deleteEntity(
      `${`${DYNAMIC_FORM_ENDPOINT}/${id}/page/${dynamicFormPages[selectedPage].Id}`}`,
      config
    );

    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
    setSelectedPage(0);
    setShowDeletePageModal(false);
    getDynamicFormPages();
  };

  useEffect(() => {
    if (id) {
      const dynamicFormDataObj = dynamicFormData.Data.find(
        (dynamicFormObj) => dynamicFormObj.Id === Number(id)
      );
      setDynamicFormModel(dynamicFormDataObj);
      setSelectedPage(0);
      getDynamicFormPages();
    }
  }, [id]);

  useEffect(() => {
    if (id && dynamicFormPages?.length > 0) {
      getDynamicFormPageFields();
    }
  }, [id, selectedPage, dynamicFormPages]);

  useEffect(() => {
    setFormFieldDataFiltered(
      search && search.length > 0
        ? formFieldDataRaw.filter(
            (x) =>
              x?.FormField?.Title &&
              x.FormField.Title.toLowerCase().includes(search.toLowerCase())
          )
        : formFieldDataRaw
    );
  }, [search]);

  const disableSaveChangesForFormField = () => !formFieldModel?.FormField;

  const disableSaveChangesForPage = () =>
    !pageModel?.Name || pageModel?.Name.replaceAll(" ", "").length <= 0;

  const cardStyle = () => ({
    margin: "16px",
    boxShadow:
      "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
  });

  return (
    <div className="lateral_padding">
      {showDeletePageModal && (
        <DeleteConfirmationMessage
          entityName={pageModel.Name}
          entityType={localize.page}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setPageModel({});
          }}
          onClickForConfirm={() => {
            handleDeletePage();
          }}
        />
      )}
      {showDeleteFieldModal && (
        <DeleteConfirmationMessage
          entityName={formFieldModel?.FormField?.Title}
          entityType={localize.form_field}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteFieldModal(false);
            setFormFieldModel(getclearFormFieldModel());
          }}
          onClickForConfirm={() => {
            handleDeleteDynamicFormPageField();
          }}
        />
      )}
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}
      {showEditPageModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditPageModal}
          setShowModal={setShowEditPageModal}
          title={localize.edit_page}
          onClickToSubmit={() => {
            handleEditPage(pageModel);
            setShowEditPageModal(false);
          }}
          onClickToCancel={() => {
            setShowEditPageModal(false);
          }}
          Component={<AddOrEditPage />}
          validators={disableSaveChangesForPage()}
        />
      )}
      {showAddPageModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showAddPageModal}
          setShowModal={setShowAddPageModal}
          title={localize.add_page}
          onClickToSubmit={() => {
            handleCreatePage(pageModel);
            setShowAddPageModal(false);
            setPageModel(getClearBasicModel());
          }}
          onClickToCancel={() => {
            setShowAddPageModal(false);
            setPageModel(getClearBasicModel());
          }}
          Component={<AddOrEditPage />}
          validators={disableSaveChangesForPage()}
        />
      )}
      {showAddFormFieldModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showAddFormFieldModal}
          setShowModal={setShowAddFormFieldModal}
          title={localize.add_form_field}
          onClickToSubmit={() => {
            handleCreateDynamicFormPageField();
            setShowAddFormFieldModal(false);
          }}
          onClickToCancel={() => {
            setShowAddFormFieldModal(false);
            setFormFieldModel(getclearFormFieldModel());
          }}
          Component={
            <AddOrEditFormField
              model={formFieldModel}
              setModel={setFormFieldModel}
            />
          }
          validators={disableSaveChangesForFormField()}
        />
      )}
      {showEditFormFieldModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditFormFieldModal}
          setShowModal={setShowEditFormFieldModal}
          title={localize.edit_form_field}
          onClickToSubmit={() => {
            handleUpdateDynamicFormPageField();
            setShowEditFormFieldModal(false);
          }}
          onClickToCancel={() => {
            setFormFieldModel(getclearFormFieldModel());
            setShowEditFormFieldModal(false);
          }}
          Component={
            <AddOrEditFormField
              model={formFieldModel}
              setModel={setFormFieldModel}
            />
          }
          validators={disableSaveChangesForFormField()}
        />
      )}
      <Card variant="outlined" style={cardStyle()}>
        <CardContent>
          <div className="flex justify-between items-center content-center">
            <div>
              <PageTitleSubtitle
                useEventTitleStyle
                title={`${localize.form}: ${
                  dynamicFormModel?.Name ?? localize.no_data
                }`}
                subtitle={dynamicFormModel?.Description ?? localize.no_data}
              />
            </div>

            <div>
              <Button
                sx={{ height: "35px" }}
                color="error"
                variant="outlined"
                className="btn-success app_general_button_data h-10"
                onClick={() => {
                  navigate(`/dynamicforms`);
                }}
              >
                <ArrowBack className="text-xs" />
                <span className="text-xs mr-2">{localize.back}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Box className="lateral_padding">
        {dynamicFormPages.length === 0 ? (
          <NoDataPage
            pageTitle={localize.no_pages_added}
            showAddButton
            addButtonText={localize.add_page}
            onAddButtonClick={() => {
              setPageModel(getClearBasicModel());
              setShowAddPageModal(true);
            }}
          />
        ) : (
          <Box className="flex w-full flex-col gap-2">
            <Box className="flex w-full justify-between p-4 items-center content-center">
              <ScrollablePageTab
                noMarginTop
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
                dynamicFormPages={dynamicFormPages}
              />

              <Box>
                <Button
                  size="small"
                  sx={{ fontSize: "0.8em", height: "35px", width: "170px" }}
                  color="success"
                  variant="contained"
                  onClick={() => {
                    setPageModel(getClearBasicModel());
                    setShowAddPageModal(true);
                  }}
                >
                  <Add className="text-sm" />
                  <span className="text-xs mr-2">{localize.add_page}</span>
                </Button>
              </Box>
            </Box>

            <Box className="w-full">
              <Card variant="outlined">
                <CardContent>
                  {!loading ? (
                    <Box className="flex flex-col w-full gap-2">
                      <Box className="flex justify-between w-full">
                        <TextField
                          style={{ width: "300px" }}
                          size="small"
                          label={localize.search_by_title}
                          onChange={searcher}
                        />

                        <Box className="flex gap-2">
                          <Button
                            size="small"
                            sx={{
                              fontSize: "0.8em",
                              height: "35px",
                              marginRight: "10px",
                            }}
                            color="error"
                            variant="text"
                            type="button"
                            onClick={() => {
                              setPageModel(dynamicFormPages[selectedPage]);
                              setShowDeletePageModal(true);
                              toggleVisibleModalConfirm();
                            }}
                          >
                            <Delete className="text-sm" />
                            {localize.delete_page}
                          </Button>

                          <Button
                            size="small"
                            sx={{
                              fontSize: "0.8em",
                              width: "160px",
                              height: "35px",
                              marginRight: "10px",
                            }}
                            color="primary"
                            variant="outlined"
                            type="button"
                            onClick={() => {
                              setPageModel(dynamicFormPages[selectedPage]);
                              setShowEditPageModal(true);
                            }}
                          >
                            <Edit className="text-sm" />
                            {localize.edit_page}
                          </Button>

                          <Button
                            size="small"
                            sx={{
                              fontSize: "0.8em",
                              height: "35px",
                              width: "170px",
                            }}
                            color="success"
                            variant="contained"
                            onClick={() => {
                              setShowAddFormFieldModal(true);
                            }}
                          >
                            <Add className="text-sm" />
                            <span className="text-xs mr-2">
                              {localize.add_field}
                            </span>
                          </Button>
                        </Box>
                      </Box>

                      <Box className="w-full" sx={{ overflow: "hidden" }}>
                        <TableComponent
                          noPadding
                          firstColumnTitle={localize.actions}
                          columnTitles={[
                            localize.field_type,
                            localize.title,
                            localize.help_text,
                            localize.visible_on_mobile,
                            localize.mandatory,
                            localize.modification_date,
                            localize.modified_by,
                          ]}
                          results={
                            formFieldDataFiltered?.length > 0
                              ? formFieldDataFiltered?.map((item) => ({
                                  key: item.Id,
                                  data: [
                                    <div>
                                      <ActionButtons
                                        onClickForEditButton={() => {
                                          setFormFieldModel({
                                            ...item,
                                            FormField: item.FormField,
                                            FieldId: item.FormField.Id,
                                            ValueTypeData: {
                                              Id: item.FormField.ValueType,
                                              Name: getFieldTypeName(
                                                item.FormField.ValueType
                                              ),
                                            },
                                            Title: item.FormField.Title,
                                            Description:
                                              item.FormField.Description,
                                            AvailableOnReview:
                                              item.AvailableOnReview,
                                            Mandatory: item.Mandatory,
                                            VisibleOnMobile:
                                              item.VisibleOnMobile,
                                          });
                                          setShowEditFormFieldModal(true);
                                        }}
                                        onclickForDeleteButton={() => {
                                          setFormFieldModel(item);
                                          setShowDeleteFieldModal(true);
                                          toggleVisibleModalConfirm();
                                        }}
                                      />
                                    </div>,
                                    getFieldTypeName(item.FormField.ValueType),
                                    item.FormField.Title,
                                    <ValueOrNoDataLabel
                                      data={item.FormField.Description}
                                    />,
                                    <TrueFalseImage
                                      value={item.VisibleOnMobile}
                                    />,
                                    <TrueFalseImage value={item.Mandatory} />,
                                    new Date(
                                      item.ModificationDate
                                    ).toLocaleString(undefined, {
                                      year: "numeric",
                                      month: "2-digit",
                                      day: "2-digit",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      second: "2-digit",
                                    }),
                                    item.CreatedBy,
                                  ],
                                }))
                              : [generateNoDataValues(8)]
                          }
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Box className="flex flex-col w-full justify-center items-center">
                      <LoadingPanel donotUseHscreen />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}

        {/* <div className="flex w-full mx-5 gap-2">
            <div className="flex flex-col" style={{ height: "100%" }}>
              <ReportSectionTitle hideMarginTop title={localize.form_pages} />
              <Card variant="outlined" style={{ height: "100% !important" }}>
                <CardContent
                  sx={{ padding: "10px !important", height: "100% !important" }}
                >
                  <div className="flex flex-col gap-2">
                    <ActionButtons
                      style={"flex w-full"}
                      hideDeleteButton
                      hideEditButton
                      buttons={[
                        {
                          name: localize.add_page,
                          icon: <Add className="text-sm" />,
                          action: () => {
                            setPageModel(getClearBasicModel());
                            setShowAddPageModal(true);
                          },
                        },
                      ]}
                    />
                    {dynamicFormPages &&
                      dynamicFormPages.length > 0 &&
                      dynamicFormPages.map((page, index) => (
                        <Chip
                          key={page.Id}
                          onDelete={() => {
                            setPageModel(dynamicFormPages[index]);
                            setShowDeletePageModal(true);
                            toggleVisibleModalConfirm();
                          }}
                          onClick={() => {
                            setSelectedPage(index);
                          }}
                          sx={{
                            "&&:hover": {
                              color:
                                selectedPage === index
                                  ? "white"
                                  : "rgba(0, 0, 0, 0.87)",
                              backgroundColor:
                                selectedPage === index
                                  ? "#10366E"
                                  : "rgba(0, 0, 0, 0.08)",
                            },
                            "& .MuiChip-deleteIcon": {
                              color: `${
                                selectedPage === index
                                  ? "white"
                                  : "rgba(0, 0, 0, 0.87)"
                              }`,
                            },
                            "& .MuiChip-deleteIcon:hover": {
                              color: `${
                                selectedPage === index
                                  ? "white"
                                  : "rgba(0, 0, 0, 0.87)"
                              }`,
                            },
                            width: "100%",
                            backgroundColor: `${
                              selectedPage === index
                                ? "#10366E"
                                : "rgba(0, 0, 0, 0.08)"
                            }`,
                            color: `${
                              selectedPage === index
                                ? "white"
                                : "rgba(0, 0, 0, 0.87)"
                            }`,
                          }}
                          label={page.Name}
                        />
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="w-full">
              <Card variant="outlined">
                <CardContent>
                  <div className="w-full flex flex-col gap-2">
                    <div className="flex justify-between">
                      <TextField
                        className="w-full"
                        style={{ width: "300px" }}
                        size="small"
                        label={localize.search_by_title}
                        onChange={searcher}
                      />
                      <div className="flex justify-end">
                        <Button
                          size="small"
                          sx={{
                            fontSize: "0.8em",
                            width: "160px",
                            marginRight: "10px",
                          }}
                          color="primary"
                          variant="outlined"
                          type="button"
                          onClick={() => {
                            setPageModel(dynamicFormPages[selectedPage]);
                            setShowEditPageModal(true);
                          }}
                        >
                          <Edit className="text-sm" />
                          {localize.edit_page}
                        </Button>

                        <Button
                          size="small"
                          sx={{ fontSize: "0.8em" }}
                          color="success"
                          variant="contained"
                          onClick={() => {
                            setShowAddFormFieldModal(true);
                          }}
                        >
                          <Add className="text-sm" />
                          <span className="text-xs mr-2">
                            {localize.add_field}
                          </span>
                        </Button>
                      </div>
                    </div>

                    {!loading ? (
                      <TableComponent
                        noPadding
                        firstColumnTitle={localize.actions}
                        columnTitles={[
                          localize.field_type,
                          localize.title,
                          localize.help_text,
                          localize.visible_on_mobile,
                          localize.mandatory,
                          localize.modification_date,
                          localize.modified_by,
                        ]}
                        results={
                          formFieldDataFiltered?.length > 0
                            ? formFieldDataFiltered?.map((item) => ({
                                key: item.Id,
                                data: [
                                  <div>
                                    <ActionButtons
                                      onClickForEditButton={() => {
                                        setFormFieldModel({
                                          ...item,
                                          FormField: item.FormField,
                                          FieldId: item.FormField.Id,
                                          ValueTypeData: {
                                            Id: item.FormField.ValueType,
                                            Name: getFieldTypeName(
                                              item.FormField.ValueType
                                            ),
                                          },
                                          Title: item.FormField.Title,
                                          Description:
                                            item.FormField.Description,
                                          AvailableOnReview:
                                            item.AvailableOnReview,
                                          Mandatory: item.Mandatory,
                                          VisibleOnMobile: item.VisibleOnMobile,
                                        });
                                        setShowEditFormFieldModal(true);
                                      }}
                                      onclickForDeleteButton={() => {
                                        setFormFieldModel(item);
                                        setShowDeleteFieldModal(true);
                                        toggleVisibleModalConfirm();
                                      }}
                                    />
                                  </div>,
                                  getFieldTypeName(item.FormField.ValueType),
                                  item.FormField.Title,
                                  <ValueOrNoDataLabel
                                    data={item.FormField.Description}
                                  />,
                                  <TrueFalseImage
                                    value={item.VisibleOnMobile}
                                  />,
                                  <TrueFalseImage value={item.Mandatory} />,
                                  new Date(
                                    item.ModificationDate
                                  ).toLocaleString(undefined, {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                  }),
                                  item.CreatedBy,
                                ],
                              }))
                            : [generateNoDataValues(8)]
                        }
                      />
                    ) : (
                      <div className="flex flex-col w-full justify-center items-center">
                        <LoadingPanel donotUseHscreen />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div> */}
      </Box>
    </div>
  );
};

export default FormBuilder;
